import type { AnalyticsShortlinkDisposition } from "../../../api.ts";
import type { TrendGranularity, TrendRange } from "../../../components/StatsTrendControls.tsx";

/** 多群折线最多画几条，防止 legend/SVG 撑裂页面 */
export const CHART_SERIES_TOP_N = 8;

/** 广告/地域条图最多条数 */
export const BAR_ITEMS_TOP_N = 15;

/** 表分页默认每页 */
export const STATS_TABLE_PAGE_SIZE = 20;

/** 分群折线配色（循环） */
export const CHAT_LINE_COLORS = [
  "blue.6",
  "teal.6",
  "orange.6",
  "violet.6",
  "green.6",
  "red.6",
  "cyan.6",
  "pink.6",
  "indigo.6",
  "lime.7",
  "grape.6",
  "yellow.7",
] as const;

export type ChatFunnelMetric = "sent" | "joined" | "visits" | "skipped";

export interface AnalyticsOverview {
  readonly totalVisits: number;
  readonly attributedVisits: number;
  readonly cookieReuseVisits: number;
  /** 归因入群事件数（兼容旧字段名 joinedClicks） */
  readonly joinedClicks: number;
  readonly joinEvents?: number;
  readonly joinUsers?: number;
  readonly conversionsSent: number;
  readonly conversionsPending: number;
  readonly conversionsFailed: number;
  readonly conversionsSkipped: number;
  readonly joinRatePct: number | null;
  readonly reportRatePct: number | null;
  /** skipped / 归因入群 */
  readonly skipRatePct?: number | null;
}

export interface DispositionCount {
  readonly disposition: string;
  readonly count: number;
}

export interface DispositionTrendItem {
  readonly bucket: string;
  readonly attributed: number;
  readonly cookieReuse: number;
  readonly nonHuman: number;
  readonly rateLimited: number;
  readonly sharedFallback: number;
  readonly perAdPrimary: number;
  readonly perAdFallback: number;
  readonly naturalFallback: number;
  readonly macroInvalid: number;
}

export interface FunnelTrendItem {
  readonly bucket: string;
  readonly visits: number;
  readonly joined: number;
  readonly sent: number;
  readonly skipped?: number;
}

export interface ChatFunnelSeries {
  readonly chatId: string;
  readonly chatTitle: string;
  readonly points: ReadonlyArray<{
    readonly bucket: string;
    readonly visits: number;
    readonly joined: number;
    readonly sent: number;
    readonly skipped?: number;
  }>;
  readonly totals: {
    readonly visits: number;
    readonly joined: number;
    readonly joinUsers?: number;
    readonly sent: number;
    readonly skipped?: number;
  };
}

export interface TopAdItem {
  readonly adId: string;
  readonly visits: number;
  readonly joined: number;
  readonly sent: number;
  readonly skipped?: number;
}

export interface CountryCount {
  readonly country: string;
  readonly count: number;
}

export interface ConversionStatusCount {
  readonly status: string;
  readonly count: number;
}

export interface MemberReconcilePoint {
  readonly bucket: string;
  readonly memberCount: number;
  readonly memberDelta: number | null;
  readonly ourJoined: number;
  readonly gap: number | null;
}

export interface MemberReconcileSeries {
  readonly chatId: string;
  readonly chatTitle: string;
  readonly points: ReadonlyArray<MemberReconcilePoint>;
}

export interface OpsAlertItem {
  readonly level: "error" | "warning" | "info";
  readonly code: string;
  readonly title: string;
  readonly hint: string;
  readonly count: number;
  readonly tabHint: "conversions" | "credentials" | "diagnosis" | "clicks" | "overview";
}

export interface FailedReasonCount {
  readonly reason: string;
  readonly count: number;
}

export interface OpsAlertsPayload {
  readonly thresholds: {
    readonly macroInvalid: number;
    readonly sharedFallback: number;
    readonly perAdFallback: number;
    readonly fallbackJoins: number;
    readonly pendingBacklog: number;
  };
  readonly failedByReason: ReadonlyArray<FailedReasonCount>;
  readonly alerts: ReadonlyArray<OpsAlertItem>;
}

export interface AnalyticsStatsResponse {
  readonly range: TrendRange;
  readonly days: number;
  readonly granularity: TrendGranularity;
  readonly timezone: string;
  readonly statsDay: string;
  /** ISO UTC 窗口起止（业务查询用） */
  readonly windowStartAt?: string;
  readonly windowEndAt?: string;
  readonly filters: {
    readonly chatId: string | null;
    readonly adId: string | null;
  };
  readonly overview: AnalyticsOverview;
  readonly dispositionBreakdown: ReadonlyArray<DispositionCount>;
  readonly visitTrend: ReadonlyArray<DispositionTrendItem>;
  readonly funnelTrend: ReadonlyArray<FunnelTrendItem>;
  readonly funnelTrendByChat?: ReadonlyArray<ChatFunnelSeries>;
  readonly conversionStatus: ReadonlyArray<ConversionStatusCount>;
  readonly opsAlerts?: OpsAlertsPayload;
  readonly topAds: ReadonlyArray<TopAdItem>;
  readonly countries: ReadonlyArray<CountryCount>;
  readonly memberReconcile: ReadonlyArray<MemberReconcileSeries>;
  readonly untrackedJoinBreakdown: ReadonlyArray<{
    readonly source: string;
    readonly count: number;
  }>;
  readonly untrackedJoinTopLinks: ReadonlyArray<{
    readonly inviteLinkKey: string;
    readonly joinSource: string;
    readonly count: number;
  }>;
}

export interface ChatOption {
  readonly chatId: number;
  readonly title: string | null;
  readonly chatToken: string;
}

export interface ChatsResponse {
  readonly items: ReadonlyArray<ChatOption>;
}

export const DISPOSITION_LABELS: Record<AnalyticsShortlinkDisposition, string> = {
  attributed: "正常归因",
  cookie_reuse: "Cookie复用",
  non_human: "非真人",
  rate_limited: "限流跳过",
  shared_fallback: "共享兜底",
  per_ad_primary: "Per-ad主链",
  per_ad_fallback: "Per-ad降级",
  natural_fallback: "自然兜底",
  macro_invalid: "宏未替换",
};

export const DISPOSITION_COLORS: Record<AnalyticsShortlinkDisposition, string> = {
  attributed: "green.6",
  cookie_reuse: "blue.6",
  non_human: "gray.6",
  rate_limited: "orange.6",
  shared_fallback: "yellow.6",
  per_ad_primary: "cyan.6",
  per_ad_fallback: "teal.6",
  natural_fallback: "gray.6",
  macro_invalid: "red.6",
};

export const CONV_STATUS_LABELS: Record<string, string> = {
  pending: "待上报",
  sent: "已上报",
  failed: "失败",
  skipped: "跳过",
};

export const CONV_STATUS_COLORS: Record<string, string> = {
  pending: "orange.6",
  sent: "green.6",
  failed: "red.6",
  skipped: "gray.6",
};

export const fmtPct = (v: number | null): string => (v === null ? "-" : `${v}%`);

export const csvCell = (v: string | number): string => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
};

export function chatSeriesKey(id: string): string {
  return `c_${id}`;
}

export function reconcileSeriesKey(id: string): string {
  return `r_${id}`;
}
